/**
 * One expense: details, approval rounds, documents, history, and the actions the caller may take now.
 *
 * Buttons are shown only for transitions the server would accept for this person (status + role + assignment),
 * so no control is offered that is certain to fail. The server still decides: every action is an RPC that
 * re-checks access, status, row version, self-approval and reasons.
 */
import { useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Modal from "../common/Modal";
import Button from "../common/Button";
import ExpenseFormModal from "./ExpenseFormModal";
import ActualFormModal from "./ActualFormModal";
import { useAuth } from "../../contexts/AuthContext";
import {
  apReturnExpense,
  cancelExpense,
  overrideDecision,
  voidExportItem,
  cloneExpense,
  decideExpense,
  documentUrl,
  FINANCE_DOCUMENT_ACCEPT,
  financeErrorMessage,
  getExpense,
  getFinanceAccess,
  getFinanceLookups,
  listApprovalRounds,
  listDocuments,
  listExpenseExportItems,
  listHistory,
  listStaffDirectory,
  money,
  payeeLabel,
  personName,
  reassignApproval,
  removeDocument,
  STATUS_CLASS,
  STATUS_LABEL,
  submitExpense,
  uploadDocument,
} from "../../services/finance.service";
import type {
  DocumentKind,
  Expense,
  StaffPerson,
} from "../../types/finance.types";

interface Props {
  expenseId: string | null;
  onClose: () => void;
  onChanged?: () => void;
}

type Action =
  | { kind: "submit" }
  | { kind: "cancel" }
  | { kind: "approved" | "returned" | "rejected" }
  | { kind: "reassign" }
  | { kind: "override-approved" | "override-returned" }
  | { kind: "ap-return" }
  | { kind: "void-export" }
  | { kind: "remove-doc"; documentId: string; fileName: string };

const inputCls =
  "mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500";

const ACTION_LABEL: Record<string, string> = {
  created: "Created",
  updated: "Edited",
  submit: "Submitted for approval",
  approve: "Approved",
  return: "Returned for changes",
  reject: "Rejected",
  cancel: "Cancelled",
  reassigned: "Approver reassigned",
  document_added: "Document added",
  document_removed: "Document removed",
  copied: "Copied to a new draft",
  actualize: "Actual recorded",
  escalated: "Escalated",
  override: "Finance override",
  export: "Exported to Business Central",
  ap_return: "Returned by AP for correction",
  void_export: "Export voided for re-export",
};

export default function ExpenseDetailModal({
  expenseId,
  onClose,
  onChanged,
}: Props) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const me = user?.id ?? "";
  const [action, setAction] = useState<Action | null>(null);
  const [comment, setComment] = useState("");
  const [assignee, setAssignee] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [docKind, setDocKind] = useState<DocumentKind>("quote");
  const fileRef = useRef<HTMLInputElement>(null);

  const expenseQ = useQuery({
    queryKey: ["finance-expense", expenseId],
    queryFn: () => getExpense(expenseId!),
    enabled: !!expenseId,
  });
  const x = expenseQ.data ?? null;
  const accessQ = useQuery({
    queryKey: ["finance-access-detail", me, x?.event_id ?? null],
    queryFn: () => getFinanceAccess(x?.event_id ?? null),
    enabled: !!x,
  });
  const lookupsQ = useQuery({
    queryKey: ["finance-lookups"],
    queryFn: getFinanceLookups,
    enabled: !!x,
    staleTime: 60_000,
  });
  // Just this expense's payee (name + vendor number), never the whole payee table.
  const payeeQ = useQuery({
    queryKey: ["finance-payee-label", x?.payee_id ?? null],
    queryFn: () => payeeLabel(x!.payee_id),
    enabled: !!x?.payee_id,
    staleTime: 60_000,
  });
  const peopleQ = useQuery({
    queryKey: ["finance-staff"],
    queryFn: listStaffDirectory,
    enabled: !!x,
    staleTime: 300_000,
  });
  const roundsQ = useQuery({
    queryKey: ["finance-rounds", expenseId],
    queryFn: () => listApprovalRounds(expenseId!),
    enabled: !!x,
  });
  const docsQ = useQuery({
    queryKey: ["finance-docs", expenseId],
    queryFn: () => listDocuments(expenseId!),
    enabled: !!x,
  });
  const histQ = useQuery({
    queryKey: ["finance-history", expenseId],
    queryFn: () => listHistory(expenseId!),
    enabled: !!x,
  });

  const people = useMemo(
    () =>
      new Map<string, StaffPerson>((peopleQ.data ?? []).map((p) => [p.id, p])),
    [peopleQ.data],
  );
  const lk = lookupsQ.data;
  const access = accessQ.data;
  const isAdmin = !!access?.is_finance_admin;
  // CRM invoice numbers this expense was exported under (finance admins only; migration 982)
  const exportItemsQ = useQuery({
    queryKey: ["finance-expense-export-items", expenseId],
    queryFn: () => listExpenseExportItems(expenseId!),
    enabled: !!x && isAdmin,
  });
  const bcNumbers = (exportItemsQ.data ?? []).filter((i) => i.bc_document_no);
  const eventCancelled = !!access?.event_cancelled;
  const canWork =
    !!x &&
    !!access &&
    (isAdmin ||
      x.created_by === me ||
      // 1141: event_work_access, not event_access -- the admin "every event" grant is view + add own only
      (!!x.event_id && access.event_work_access === true));
  const viewOnly = !!x && !!access && !canWork && access.event_access === true;
  const openRound =
    (roundsQ.data ?? []).find((r) => r.decision === null) ?? null;
  // either listed approver (default or alternate) may decide; the original approver too after an escalation
  const iAmApprover =
    !!openRound &&
    (openRound.assignee_id === me ||
      openRound.alternate_assignee_id === me ||
      ((openRound.escalation_level ?? 0) > 0 &&
        openRound.original_assignee_id === me));
  const actualStage = x?.status === "actual_review_pending";
  const [actualOpen, setActualOpen] = useState(false);
  const editable = !!x && ["draft", "returned"].includes(x.status);

  const refresh = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ["finance-expense", expenseId] }),
      qc.invalidateQueries({ queryKey: ["finance-rounds", expenseId] }),
      qc.invalidateQueries({ queryKey: ["finance-docs", expenseId] }),
      qc.invalidateQueries({ queryKey: ["finance-history", expenseId] }),
      qc.invalidateQueries({ queryKey: ["finance-expenses"] }),
      qc.invalidateQueries({ queryKey: ["finance-budget"] }),
      qc.invalidateQueries({ queryKey: ["finance-approvals"] }),
    ]);
    onChanged?.();
  };

  const openAction = (a: Action) => {
    setError(null);
    setComment("");
    if (a.kind === "submit" && x) {
      const dept = lk?.departments.find((d) => d.id === x.department_id);
      setAssignee(dept?.default_approver_profile_id ?? "");
    } else {
      setAssignee("");
    }
    setAction(a);
  };

  const run = async () => {
    if (!x || !action) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (action.kind === "submit") {
        const dept = lk?.departments.find((d) => d.id === x.department_id);
        const chosen =
          isAdmin && assignee && assignee !== dept?.default_approver_profile_id
            ? assignee
            : null;
        await submitExpense(x, chosen, comment.trim() || null);
      } else if (action.kind === "cancel") {
        await cancelExpense(x, comment.trim());
      } else if (action.kind === "reassign") {
        await reassignApproval(x, assignee, comment.trim());
      } else if (action.kind === "remove-doc") {
        await removeDocument(action.documentId, comment.trim());
      } else if (
        action.kind === "override-approved" ||
        action.kind === "override-returned"
      ) {
        await overrideDecision(
          x,
          action.kind === "override-approved" ? "approved" : "returned",
          comment.trim(),
        );
      } else if (action.kind === "ap-return") {
        await apReturnExpense(x, comment.trim());
      } else if (action.kind === "void-export") {
        await voidExportItem(x.id, comment.trim());
      } else {
        await decideExpense(x, action.kind, comment.trim() || null);
      }
      setAction(null);
      await refresh();
    } catch (e) {
      setError(financeErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const doClone = async () => {
    if (!x) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await cloneExpense(x);
      await refresh();
    } catch (e) {
      setError(financeErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const onFile = async (files: FileList | null) => {
    if (!x || !files || files.length === 0) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      for (const f of Array.from(files)) {
        await uploadDocument(x.id, f, docKind);
      }
      await refresh();
    } catch (e) {
      setError(financeErrorMessage(e));
    } finally {
      setBusy(false);
      if (fileRef.current) {
        fileRef.current.value = "";
      }
    }
  };

  const openDoc = async (path: string) => {
    try {
      window.open(await documentUrl(path), "_blank", "noopener");
    } catch (e) {
      setError(financeErrorMessage(e));
    }
  };

  const needsComment =
    action?.kind === "cancel" ||
    action?.kind === "returned" ||
    action?.kind === "rejected" ||
    action?.kind === "reassign" ||
    action?.kind === "remove-doc" ||
    action?.kind === "override-approved" ||
    action?.kind === "override-returned" ||
    action?.kind === "ap-return" ||
    action?.kind === "void-export";

  const name = (
    code: string | null | undefined,
    list: { code?: string; id?: string; name: string }[] | undefined,
  ) => list?.find((i) => i.code === code || i.id === code)?.name ?? code ?? "—";

  const row = (label: string, value: React.ReactNode) => (
    <div>
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className="text-sm text-gray-900">{value}</dd>
    </div>
  );

  const canAttach =
    !!x &&
    canWork &&
    ["draft", "returned", "pending_approval", "approved"].includes(x.status);

  return (
    <Modal
      isOpen={!!expenseId}
      onClose={onClose}
      title={x ? `Expense ${x.expense_no}` : "Expense"}
      size="xl"
    >
      {!x ? (
        <p className="py-6 text-sm text-gray-500">
          {expenseQ.isLoading
            ? "Loading…"
            : "This expense is not available to you."}
        </p>
      ) : (
        <div className="space-y-6" data-testid="finance-expense-detail">
          <div className="flex flex-wrap items-center gap-3">
            <span
              className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_CLASS[x.status]}`}
            >
              {STATUS_LABEL[x.status]}
            </span>
            {["pending_approval", "actual_review_pending"].includes(x.status) &&
              openRound && (
                <span className="text-sm text-gray-600">
                  Waiting for {personName(people, openRound.assignee_id)}
                  {openRound.alternate_assignee_id
                    ? ` or ${personName(people, openRound.alternate_assignee_id)}`
                    : ""}
                  {openRound.escalation_target === "manager"
                    ? " (escalated to manager)"
                    : ""}
                  {openRound.escalation_target === "finance_admin_queue"
                    ? " (in the finance-admin queue)"
                    : ""}
                </span>
              )}
            {x.supersedes_id && (
              <span className="text-xs text-gray-500">
                Resubmission of a rejected expense
              </span>
            )}
          </div>

          <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {row("Anticipated amount", money(x.anticipated_amount))}
            {row(
              "Approved amount",
              x.approved_amount !== null ? money(x.approved_amount) : "—",
            )}
            {row("Service date", x.anticipated_service_date)}
            {row(
              "Expected payment",
              <>
                {x.est_payment_date ?? "—"}
                {x.est_payment_overridden && (
                  <span className="block text-xs text-amber-700">
                    Changed from {x.est_payment_date_derived}:{" "}
                    {x.est_payment_override_reason}
                  </span>
                )}
              </>,
            )}
            {row("Type", name(x.expense_type_code, lk?.types))}
            {row(
              x.payee_type === "employee" ? "Employee" : "Vendor",
              <span data-testid="finance-expense-payee">
                {payeeQ.data ?? "—"}
              </span>,
            )}
            {row("Department", name(x.department_id, lk?.departments))}
            {row(
              "Billing category",
              name(x.billing_category_id, lk?.categories),
            )}
            {row(
              "GL account",
              <>
                <span className="font-mono">
                  {x.gl_account_no ?? "Not mapped"}
                </span>
                {x.gl_overridden && (
                  <span className="block text-xs text-amber-700">
                    Override: {x.gl_override_reason}
                  </span>
                )}
              </>,
            )}
            {row("State", x.state ?? "—")}
            {row("Created by", personName(people, x.created_by))}
            {row("Created", new Date(x.created_at).toLocaleString())}
          </dl>
          {viewOnly && (
            <p
              className="rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-600"
              data-testid="finance-detail-view-only"
            >
              View only: you can see this expense, but only the person who
              created it, the event&apos;s staff or finance can change, submit
              or cancel it.
            </p>
          )}
          <div>
            <dt className="text-xs text-gray-500">Description</dt>
            <p className="whitespace-pre-wrap text-sm text-gray-900">
              {x.description}
            </p>
            {x.cancel_reason && (
              <p className="mt-2 text-sm text-gray-600">
                Cancelled: {x.cancel_reason}
              </p>
            )}
          </div>

          {x.actual_amount !== null && x.actual_amount !== undefined && (
            <section
              className="rounded-md border border-gray-200 p-3"
              data-testid="finance-actual-section"
            >
              <h4 className="mb-2 text-sm font-semibold text-gray-900">
                Actual / invoice
              </h4>
              <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                {row("Actual amount", money(x.actual_amount))}
                {row(
                  "Variance",
                  <span
                    className={
                      Number(x.variance_amount) > 0
                        ? "text-red-700"
                        : "text-gray-900"
                    }
                  >
                    {money(x.variance_amount)} ({x.variance_pct ?? 0}%)
                  </span>,
                )}
                {row(
                  "Invoice",
                  `${x.invoice_number ?? "—"} · ${x.invoice_date ?? "—"}`,
                )}
                {isAdmin &&
                  bcNumbers.length > 0 &&
                  row(
                    "CRM invoice no. (BC)",
                    <span data-testid="finance-expense-bc-document">
                      {bcNumbers.map((i) => (
                        <span key={i.id} className="block font-mono">
                          {i.bc_document_no}
                          {i.voided_at ? " (voided)" : ""}
                        </span>
                      ))}
                    </span>,
                  )}
                {row(
                  "Due date",
                  <>
                    {x.actual_due_date ?? "—"}
                    {x.actual_due_overridden && (
                      <span className="block text-xs text-amber-700">
                        Terms gave {x.actual_due_date_derived}:{" "}
                        {x.actual_due_override_reason}
                      </span>
                    )}
                  </>,
                )}
                {row("Service date", x.actual_service_date ?? "—")}
                {row(
                  "Approval of the actual",
                  x.actual_auto_approved
                    ? "Auto-approved (within the limit)"
                    : x.actual_approved_by
                      ? `Approved by ${personName(people, x.actual_approved_by)}`
                      : "Needs the approver",
                )}
              </dl>
              {x.change_flags &&
                Object.values(x.change_flags).some(Boolean) && (
                  <p className="mt-2 text-xs text-amber-700">
                    Changed since approval:{" "}
                    {Object.entries(x.change_flags)
                      .filter(([, v]) => v)
                      .map(([k]) =>
                        k === "gl"
                          ? "GL account"
                          : k === "terms"
                            ? "payment terms"
                            : k,
                      )
                      .join(", ")}
                  </p>
                )}
              {(x.reapproval_reasons ?? []).length > 0 && (
                <p className="mt-1 text-xs text-gray-600">
                  Sent to the approver:{" "}
                  {(x.reapproval_reasons ?? []).join("; ")}
                </p>
              )}
              {x.actual_notes && (
                <p className="mt-1 text-sm text-gray-700">{x.actual_notes}</p>
              )}
            </section>
          )}

          {eventCancelled &&
            ["draft", "returned", "rejected"].includes(x.status) && (
              <p
                className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
                data-testid="finance-detail-event-cancelled"
              >
                The event is cancelled, so this expense cannot be submitted or
                copied. You can still cancel it.
              </p>
            )}

          {/* Actions */}
          <div className="flex flex-wrap gap-2 border-t pt-4">
            {editable && canWork && (
              <>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setEditOpen(true)}
                >
                  Edit
                </Button>
                {!eventCancelled && (
                  <Button
                    size="sm"
                    onClick={() => openAction({ kind: "submit" })}
                  >
                    Submit for approval
                  </Button>
                )}
              </>
            )}
            {iAmApprover && (
              <>
                <Button
                  size="sm"
                  onClick={() => openAction({ kind: "approved" })}
                >
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => openAction({ kind: "returned" })}
                >
                  Return for changes
                </Button>
                {!actualStage && (
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => openAction({ kind: "rejected" })}
                  >
                    Reject
                  </Button>
                )}
              </>
            )}
            {canWork && ["approved", "actual_returned"].includes(x.status) && (
              <Button
                size="sm"
                onClick={() => setActualOpen(true)}
                data-testid="finance-record-actual"
              >
                {x.status === "approved"
                  ? "Record actual / invoice"
                  : "Correct the actual"}
              </Button>
            )}
            {isAdmin &&
              ["pending_approval", "actual_review_pending"].includes(
                x.status,
              ) && (
                <>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => openAction({ kind: "override-approved" })}
                  >
                    Override: approve
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => openAction({ kind: "override-returned" })}
                  >
                    Override: return
                  </Button>
                </>
              )}
            {isAdmin && x.status === "ready_for_export" && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => openAction({ kind: "ap-return" })}
              >
                Return for correction (AP)
              </Button>
            )}
            {isAdmin && x.status === "exported" && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => openAction({ kind: "void-export" })}
              >
                Void export (re-export)
              </Button>
            )}
            {isAdmin &&
              ["pending_approval", "actual_review_pending"].includes(
                x.status,
              ) && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => openAction({ kind: "reassign" })}
                >
                  Reassign approver
                </Button>
              )}
            {canWork &&
              ["draft", "returned", "approved", "actual_returned"].includes(
                x.status,
              ) && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => openAction({ kind: "cancel" })}
                >
                  Cancel expense
                </Button>
              )}
            {canWork && x.status === "rejected" && !eventCancelled && (
              <Button
                size="sm"
                variant="secondary"
                onClick={doClone}
                disabled={busy}
              >
                Copy to a new draft
              </Button>
            )}
          </div>

          {action && (
            <div
              className="rounded-md border border-gray-200 bg-gray-50 p-4 space-y-3"
              data-testid="finance-action-panel"
            >
              <p className="text-sm font-medium text-gray-900">
                {action.kind === "submit" && "Submit for approval"}
                {action.kind === "approved" &&
                  (actualStage
                    ? "Approve the revised actual amount"
                    : "Approve this expense")}
                {action.kind === "returned" && "Return for changes"}
                {action.kind === "rejected" && "Reject this expense"}
                {action.kind === "cancel" && "Cancel this expense"}
                {action.kind === "reassign" && "Reassign the approver"}
                {action.kind === "remove-doc" && `Remove ${action.fileName}`}
                {action.kind === "override-approved" &&
                  "Finance override: approve (recorded with your name and reason)"}
                {action.kind === "override-returned" &&
                  "Finance override: return to the initiator (recorded)"}
                {action.kind === "ap-return" &&
                  "Return to the initiator for correction"}
                {action.kind === "void-export" &&
                  "Void this row's export so it can be exported again"}
              </p>
              {action.kind === "void-export" && (
                <p className="text-sm text-amber-800">
                  The re-export gets a NEW CRM invoice number. Make sure the
                  invoice under the old number is not posted in Business Central
                  (delete it there), or the cost is booked twice.
                </p>
              )}
              {action.kind === "submit" && (
                <p className="text-sm text-gray-600">
                  It goes to the department approver
                  {lk?.departments.find((d) => d.id === x.department_id)
                    ?.default_approver_profile_id
                    ? `: ${personName(people, lk?.departments.find((d) => d.id === x.department_id)?.default_approver_profile_id)}`
                    : " (none set yet — finance must set one before this can be submitted)"}
                  .
                  {!x.gl_account_no && (
                    <span className="block text-amber-700">
                      No GL account is mapped for this department and billing
                      category yet, so it cannot be submitted. Ask finance to
                      add the mapping, then edit and save the expense.
                    </span>
                  )}
                </p>
              )}
              {action.kind === "approved" && (
                <p className="text-sm text-gray-600">
                  {actualStage
                    ? `Approving accepts the actual of ${money(x.actual_amount)} (approved estimate ${money(x.approved_amount)}) and makes it ready for export.`
                    : `Approving authorizes the anticipated spend of ${money(x.anticipated_amount)}. It does not pay anything.`}
                </p>
              )}
              {((action.kind === "submit" && isAdmin) ||
                action.kind === "reassign") && (
                <div>
                  <label
                    className="block text-sm font-medium text-gray-700"
                    htmlFor="fx-assignee"
                  >
                    {action.kind === "submit"
                      ? "Approver (finance may choose another)"
                      : "New approver"}
                  </label>
                  <select
                    id="fx-assignee"
                    className={inputCls}
                    value={assignee}
                    onChange={(e) => setAssignee(e.target.value)}
                  >
                    <option value="">Choose…</option>
                    {(peopleQ.data ?? [])
                      .filter((p) => p.id !== x.created_by && p.can_use_finance)
                      .map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.full_name || p.email} ({p.role})
                        </option>
                      ))}
                  </select>
                </div>
              )}
              <div>
                <label
                  className="block text-sm font-medium text-gray-700"
                  htmlFor="fx-comment"
                >
                  {needsComment ? "Reason (required)" : "Comment (optional)"}
                </label>
                <textarea
                  id="fx-comment"
                  rows={2}
                  className={inputCls}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={
                    action.kind === "rejected" || action.kind === "cancel"
                      ? "danger"
                      : "primary"
                  }
                  onClick={run}
                  disabled={
                    busy ||
                    (needsComment && !comment.trim()) ||
                    (action.kind === "reassign" && !assignee) ||
                    (action.kind === "submit" &&
                      (!assignee || !x.gl_account_no))
                  }
                >
                  {busy ? "Working…" : "Confirm"}
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setAction(null)}
                  disabled={busy}
                >
                  Back
                </Button>
              </div>
            </div>
          )}
          {error && (
            <div
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800"
            >
              {error}
            </div>
          )}

          {/* Documents */}
          <section>
            <h4 className="mb-2 text-sm font-semibold text-gray-900">
              Documents
            </h4>
            {(docsQ.data ?? []).length === 0 && (
              <p className="text-sm text-gray-500">No documents yet.</p>
            )}
            <ul className="divide-y rounded-md border">
              {(docsQ.data ?? []).map((d) => (
                <li
                  key={d.id}
                  className="flex items-center justify-between px-3 py-2 text-sm"
                >
                  <div>
                    <button
                      type="button"
                      className={
                        d.deleted_at
                          ? "text-gray-400 line-through"
                          : "text-primary-600 hover:underline"
                      }
                      onClick={() => openDoc(d.storage_path)}
                    >
                      {d.file_name}
                    </button>
                    <span className="ml-2 text-xs text-gray-500">
                      {d.kind} · {personName(people, d.uploaded_by)} ·{" "}
                      {new Date(d.uploaded_at).toLocaleDateString()}
                      {d.deleted_at ? ` · removed: ${d.delete_reason}` : ""}
                    </span>
                  </div>
                  {!d.deleted_at &&
                    editable &&
                    (d.uploaded_by === me || isAdmin) && (
                      <button
                        type="button"
                        className="text-xs text-red-600 hover:underline"
                        onClick={() =>
                          openAction({
                            kind: "remove-doc",
                            documentId: d.id,
                            fileName: d.file_name,
                          })
                        }
                      >
                        Remove
                      </button>
                    )}
                </li>
              ))}
            </ul>
            {canAttach && (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <select
                  aria-label="Document kind"
                  className="rounded-md border border-gray-300 px-2 py-1 text-sm"
                  value={docKind}
                  onChange={(e) => setDocKind(e.target.value as DocumentKind)}
                >
                  <option value="quote">Quote</option>
                  <option value="contract">Contract</option>
                  <option value="invoice">Invoice</option>
                  <option value="receipt">Receipt</option>
                  <option value="other">Other</option>
                </select>
                <input
                  ref={fileRef}
                  type="file"
                  multiple
                  accept={FINANCE_DOCUMENT_ACCEPT}
                  className="text-sm"
                  data-testid="finance-doc-input"
                  disabled={busy}
                  onChange={(e) => onFile(e.target.files)}
                />
                <span className="text-xs text-gray-500">
                  PDF, image, Word or Excel, up to 25 MB.
                </span>
              </div>
            )}
          </section>

          {/* Approval rounds */}
          {(roundsQ.data ?? []).length > 0 && (
            <section>
              <h4 className="mb-2 text-sm font-semibold text-gray-900">
                Approvals
              </h4>
              <ul className="space-y-1 text-sm">
                {(roundsQ.data ?? []).map((r) => (
                  <li key={r.id} className="text-gray-700">
                    {new Date(r.requested_at).toLocaleString()}: sent to{" "}
                    {personName(people, r.original_assignee_id)}
                    {r.assignee_id !== r.original_assignee_id
                      ? `, now ${personName(people, r.assignee_id)}`
                      : ""}{" "}
                    for {money(r.amount_at_request)} —{" "}
                    {r.decision
                      ? `${r.decision} by ${personName(people, r.decided_by)}${r.decision_comment ? `: “${r.decision_comment}”` : ""}`
                      : "waiting"}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* History */}
          <section>
            <h4 className="mb-2 text-sm font-semibold text-gray-900">
              History
            </h4>
            <ol className="space-y-1 text-sm" data-testid="finance-history">
              {(histQ.data ?? []).map((h) => (
                <li key={h.id} className="text-gray-700">
                  <span className="text-gray-500">
                    {new Date(h.created_at).toLocaleString()}
                  </span>{" "}
                  <span className="font-medium">
                    {ACTION_LABEL[h.action] ?? h.action}
                  </span>{" "}
                  by {h.actor_id ? personName(people, h.actor_id) : "system"}
                  {h.reason ? ` — ${h.reason}` : ""}
                  {h.action === "updated" && h.changes ? (
                    <span className="text-gray-500">
                      {" "}
                      ({Object.keys(h.changes).join(", ").replace(/_/g, " ")})
                    </span>
                  ) : null}
                </li>
              ))}
            </ol>
          </section>

          <ActualFormModal
            isOpen={actualOpen}
            onClose={() => setActualOpen(false)}
            onSaved={async () => {
              setActualOpen(false);
              await refresh();
            }}
            expense={x as Expense}
            isFinanceAdmin={isAdmin}
          />
          <ExpenseFormModal
            isOpen={editOpen}
            onClose={() => setEditOpen(false)}
            onSaved={async () => {
              setEditOpen(false);
              await refresh();
            }}
            eventId={x.event_id}
            expense={x as Expense}
            isFinanceAdmin={isAdmin}
          />
        </div>
      )}
    </Modal>
  );
}
